import { Elevation, Lot, Model, Project } from '@zayan-erp/home-building';
import { Prospect } from './prospect.models';
import { DealTabIndex } from './deal-tab.enum';

export interface SalesAgent {
  id: string;
  name: string;
  email: string;
  isPrimarySalesAgent: boolean;
}

export enum PricingType {
  NetOfHST = 'Net of HST',
  GrossPrice = 'Gross Price'
}

export interface ModelImport {
  id: string;
  modelId?: string;
  projectModelId?: string;
  projectId: string;
  constructionTypeId?: string | null;
  scheduleId?: string | null;
  scheduleCode?: string | null;
  modelName: string;
  elev?: string | null;
  descr?: string | null;
  area?: number | null;
  bedrooms?: number | null;
  style?: string | null;
  notes?: string | null;
  longDesc?: string | null;
  discontinued?: boolean;
  sellable?: boolean;
  isEligible?: boolean;
  violatedRules?: string[];
  project: {
    id: string;
    code: string;
    name: string;
    productListId: string | null;
  };
}

export interface DealModel {
  id: string;
  modelName: string;
}

export interface Contract {
  id: string;
  dealId: string;
  unitPrice: number | null;
  unitPremiumPrice: number | null;
  baseModelPrice: number | null;
  discount: number | null;
  contractDate: string | null;
  earnestDeposit: number | null;
  finalSaleDate: string | null;
  approvalDate: string | null;
  targetClosingDate: string | null;
  actualClosingDate: string | null;
  isActive?: boolean;
  createdAt?: string;
  lastModified?: string | null;
  createdByUserId?: string;
  updatedByUserId?: string | null;
  inContractOptions: number | null;
  pendingOptions: number | null;
  totalOptions: number | null;
  total: number | null;
  eligibleForGSTRebate?: boolean;
  investmentProperty?: boolean;
}

export interface Deal {
  id: string;
  lotId: string;
  model?: DealModel | null;
  elevation?: Pick<Elevation, 'id' | 'elevationCode' | 'description'> | null;
  prospect: Pick<Prospect, 'id' | 'firstName' | 'lastName' | 'email' | 'phoneNumber'>;
  dealStatus: DealStatus;
  canCancel: boolean;
  hasActiveSigningContract: boolean;
  isActive: boolean;
  createdAt: string;
  lastModified: string | null;
  updatedByUser: string | null;
  createdByUserId: string;
  createdByUser: string;
  targetClosingDate: string;
  contractDate: string;
  updatedByUserId: string | null;
  lot: Pick<Lot, 'id' | 'lotNo' | 'projectCode' | 'address' | 'lotType' | 'floorplanFileLink'> & {
    projectId: string;
    selectedModel?: Pick<Model, 'id' | 'modelName'>;
    selectedElevation?: Pick<Elevation, 'id' | 'elevationCode' | 'description'>;
  };
  reservation?: {
    id: string;
    dealId: string;
    expiryDate: string | null;
    deposit: number | null;
    refundable: boolean | null;
  } | null;
  contract?: Contract | null;
  prospectDocuments?: ProspectDocument[];
  salesFloorAgent?: salesFloorAgent | null;
  isCFL: boolean;
}
export interface salesFloorAgent {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface DealLite {
  contractDate: string;
  dealId: string;
  dealStatus: {
    id: string;
    name: string;
  };
  elevation: {
    id: string;
    name: string;
  };
  lot: {
    id: string;
    name: string;
  };
  model: {
    id: string;
    name: string;
  };
  project: {
    id: string;
    name: string;
  };
  prospect: Pick<Prospect, 'id' | 'firstName' | 'lastName' | 'middleName' | 'email' | 'phoneNumber'>;
  salesOffice: {
    id: string;
    name: string;
  };
  targetClosingDate: string | null;
  totalSignees: number;
  signedSignees: number;
}

export interface StatusCount {
  statusId: string;
  statusName: string;
  count: number;
}

export interface DealsLiteResponse {
  deals: DealLite[];
  counts: StatusCount[];
}

export interface ProjectModelElevation {
  projectModelElevationId: string;
  elevationId: string;
  elevationCode: string;
  modelId: string;
  modelName: string;
  projectId: string;
}

export interface CancelDeal {
  cancellationDate: string;
  cancellationReasonId: string | null;
  reason: string;
  keepOptions: boolean;
  keepModel: boolean;
  deleteDeposits: boolean;
  lotTypeId: string;
}

export interface ProspectDocument {
  id: string;
  frontS3DocumentId: string;
  frontDocument: frontDocument;
  backS3DocumentId: string | null;
  backDocument: frontDocument | null;
  documentTypeId: string;
  documentTypeName: string;
}

export interface frontDocument {
  id: string;
  s3Key: string;
  directUrl: string;
}

export interface TimelineEvent {
  status: string;
  completed: boolean;
}

export interface DealStatus {
  id: string;
  name: string;
}

export type ApsDetailTab =
  | 'overview'
  | 'options'
  | 'critical-dates'
  | 'deposits'
  | 'lot-communication'
  | 'co-buyers'
  | 'realtor-agents'
  | 'documents'
  | 'miscellaneous-docs'
  | 'incentives'
  | 'reassign-buyer';

export const TAB_LABELS: Record<ApsDetailTab, string> = {
  overview: 'Overview',
  options: 'Options',
  'critical-dates': 'Critical Dates',
  deposits: 'Deposits',
  'lot-communication': 'Lot Communication',
  'co-buyers': 'Co-Buyers',
  'realtor-agents': 'Realtor Agents',
  documents: 'Documents',
  'miscellaneous-docs': 'Miscellaneous Docs',
  incentives: 'Incentives',
  'reassign-buyer': 'Reassign Buyer'
};

export const TAB_ROUTE_PATHS: Record<DealTabIndex, string> = {
  [DealTabIndex.Overview]: 'overview',
  [DealTabIndex.Options]: 'options',
  [DealTabIndex.CriticalDates]: 'critical-dates',
  [DealTabIndex.Deposits]: 'deposits',
  [DealTabIndex.LotCommunication]: 'lot-communication',
  [DealTabIndex.CoBuyers]: 'co-buyers',
  [DealTabIndex.DecorConsultant]: 'overview', // no route, fallback
  [DealTabIndex.SalesAgents]: 'overview', // no route, fallback
  [DealTabIndex.RealtorAgents]: 'realtor-agents',
  [DealTabIndex.Documents]: 'documents',
  [DealTabIndex.MiscellaneousDocs]: 'miscellaneous-docs',
  [DealTabIndex.incentives]: 'incentives',
  [DealTabIndex.OptionsLegacy]: 'options',
  [DealTabIndex.ReassignBuyer]: 'reassign-buyer'
};

export function getTabFromUrl(url: string): ApsDetailTab {
  const segment = url.split('/').filter(Boolean).pop()?.split('?')[0] ?? '';
  return (segment in TAB_LABELS ? segment : 'overview') as ApsDetailTab;
}

export const ROUTE_PATH_TO_TAB: Record<string, DealTabIndex> = {};
for (const [tabStr, path] of Object.entries(TAB_ROUTE_PATHS)) {
  const tab = Number(tabStr) as DealTabIndex;
  if (!(path in ROUTE_PATH_TO_TAB)) {
    ROUTE_PATH_TO_TAB[path] = tab;
  }
}

export function getTabIndexFromRoutePath(segment: string | undefined): DealTabIndex {
  if (segment && segment in ROUTE_PATH_TO_TAB) {
    return ROUTE_PATH_TO_TAB[segment];
  }
  return DealTabIndex.Overview;
}

export interface DealDetailLite {
  id: string;
  project: Pick<Project, 'id' | 'projectName'> & {
    projectCode: string;
    isTownhouse: boolean;
  };
  lot: Pick<Lot, 'id' | 'lotNo' | 'lotTypeId' | 'floorplanFileLink' | 'buyerDocumentLink' | 'address' | 'swing'>;
  model?: DealModel | null;
  elevation?: (Pick<Elevation, 'id' | 'elevationCode' | 'description'> & { basePrice?: number }) | null;
  dealStatus: DealStatus;
  salesAgents: Pick<SalesAgent, 'id' | 'name'>[];
  prospect: Pick<Prospect, 'id' | 'email' | 'phoneNumber'> & {
    uploadedCards: ProspectDocument[];
    name: string;
  };
  canCancel: boolean;
  hasActiveSigningContract: boolean;
  isCFL: boolean;
  pricingType?: string | null;
  salesFloorAgent?: salesFloorAgent | null;
  contract: Contract;
  reservation: Reservation;
}

export interface Reservation {
  id: string;
  dealId: string;
  expiryDate: string | null;
  deposit: number | null;
  refundable: boolean | null;
}
